import { useState, useEffect, useRef, ChangeEvent } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import {
  space,
  SpaceProps,
  LayoutProps,
  layout,
  color,
  ColorProps,
} from "styled-system";

import SearchIcon from "../SearchIcon/SearchIcon";
import RecentSearches from "./RecentSearches";

type SearchInputProps = SpaceProps & {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  savedSearches: { label: string; url: string }[];
  recentSearches: { label: string; url: string }[];
  value?: string;
  isAuthenticated: boolean;
  auctionViewRoot: string;
  vehicleType: string;
};

const StyledInput = styled.input`
  border: none;
  box-sizing: border-box;
  &:focus {
    outline: none;
    color: black;
    &::-webkit-input-placeholder {
      color: white;
    }
  }
  background: transparent;
  height: 100%;
  width: 100%;

  ${css({
    color: "primary-white",
    fontSize: 2,
    fontFamily: "normal",
    "&::placeholder": {
      color: "primary-white",
    },
  })};
`;

const StyledContainer = styled.div<
  SpaceProps & LayoutProps & { isFocused: boolean }
>`
  background: ${(props) => (props.isFocused ? "white" : "#313a51")};
  border-radius: 0 12px 6px 0;
  box-sizing: border-box;
  display: flex;
  padding-left: 15px;
  transform: skewX(-13deg);
  flex: 1;
  position: relative;
  z-index: 10;
  & > * {
    transform: skewX(13deg);
  }
  ${space}
  ${layout}
`;

const SearchIconContainer = styled.button<ColorProps>`
  display: flex;
  align-items: center;
  padding: 0 24px;
  transform: skewX(0deg);
  background: white;
  border-radius: 0 12px 6px 0;
  border: none;
  margin: 0;
  & > * {
    transform: skewX(13deg);
  }
  ${color}
`;
export default function SearchInput({
  savedSearches,
  recentSearches,
  isAuthenticated,
  auctionViewRoot,
  vehicleType,
  value,
  ...props
}: SearchInputProps) {
  const node = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [focused, setFocused] = useState(false);

  const handleOnFocus = () => {
    setFocused(true);
    if (!expanded) {
      setExpanded(true);
    }
  };
  const handleOnBlur = () => {
    setFocused(false);
  };

  const handleOutsideClick = (e: any) => {
    if (node.current && node.current.contains(e.target)) {
      return;
    }
    setExpanded(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("focusout", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("focusout", handleOutsideClick);
    };
  }, []);

  return (
    <StyledContainer
      {...props}
      isFocused={focused}
      height={{ _: "47px", desktop: "55px" }}
      ref={node}
    >
      <StyledInput
        role="input"
        placeholder="e.g. blackbushe audi a3"
        onFocus={handleOnFocus}
        onChange={(e) => props.onChange(e)}
        onBlur={handleOnBlur}
        value={value}
      ></StyledInput>
      <SearchIconContainer
        bg={focused ? "primary-light-blue" : "transparent"}
        data-en="click"
        data-ec="button"
        data-el="free-text-search"
        data-ea="search"
      >
        <SearchIcon></SearchIcon>
      </SearchIconContainer>
      {expanded && isAuthenticated && (
        <RecentSearches
          searchResultsPageUrl={""}
          savedSearches={savedSearches}
          recentSearches={recentSearches}
          auctionViewRoot={auctionViewRoot}
          close={() => {
            setExpanded(false);
          }}
        ></RecentSearches>
      )}
    </StyledContainer>
  );
}
